import * as React from "react";
import { Card, Grid, InputLabel } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import { useFields } from "@/hooks/api/use-fields";
import { useCreateGroup, useGroup, useUpdateGroup } from "@/hooks/api/use-groups";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { CustomDrawer } from "@/components/custom-drawer";
import { FilterBuilder } from "@/components/customTable/filter-builder";
import { FilterCondition, FilterOperator } from "@/components/customTable/types";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

export function CreateGroup({
	isCreateGroupDrawerOpen,
	onCreateDrawerClose,
	groupId,
}: {
	isCreateGroupDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
	groupId: string | null;
}): React.JSX.Element {
	const isEdit = !!groupId;

	const [temporaryConditions, setTemporaryConditions] = React.useState<FilterCondition[]>([]);

	const { data: group, isLoading: isGroupLoading } = useGroup(groupId || "");
	const { data: fields, isLoading: isFieldsLoading } = useFields();
	const createGroupMutation = useCreateGroup();
	const updateGroupMutation = useUpdateGroup();

	const availableFields = React.useMemo(() => {
		return [
			{ label: "Phone", name: "phone", type: "text" },
			{
				label: "Tags",
				name: "tags",
				type: "select",
				getOptionsUrl: "/crm/tags",
				fetchLabel: "label",
				fetchValue: "name",
			},
			...(fields || []),
		];
	}, [fields]);

	const methods = useForm<any>({
		defaultValues: {
			name: "",
			filter: [],
			isStatic: false,
			advancedSelection: false,
		},
	});

	const {
		handleSubmit,
		reset,
		setError,
		control,
		watch,
		formState: { isSubmitting },
	} = methods;

	const advancedSelection = watch("advancedSelection");

	React.useLayoutEffect(() => {
		if (group && isEdit) {
			reset({
				name: group.name,
				filter: group.filter,
				isStatic: group.isStatic,
				advancedSelection: !group.isStatic,
			});
			setTemporaryConditions(
				group.filter.map((condition) => {
					const field = availableFields?.find((field) => condition.field === field.name);
					return {
						...condition,
						value: Array.isArray(condition.value)
							? condition.value?.map((v) => ({ value: v, label: v }))
							: field?.type === "select"
								? [
										{
											value: condition.value,
											label:
												field.options?.options?.find((option) => option.value === condition.value)?.label ||
												condition.value,
										},
									]
								: condition.value,
					};
				})
			);
		} else {
			reset({
				name: "",
				filter: [],
				isStatic: false,
				advancedSelection: false,
			});
			setTemporaryConditions([]);
		}
	}, [group, isEdit, reset, availableFields, isCreateGroupDrawerOpen]);

	const onSubmitForm = async (values: any) => {
		try {
			const formattedConditions = temporaryConditions.map((condition) => {
				const isMultiple = condition.operator === FilterOperator.IN || condition.operator === FilterOperator.NOT_IN;
				if (isMultiple && Array.isArray(condition.value)) {
					return {
						...condition,
						value: condition.value?.map((v) => v?.value) || [],
					};
				}
				return {
					...condition,
					value: condition.value?.[0]?.value || condition?.value || "",
				};
			});

			await (isEdit && groupId
				? updateGroupMutation.mutateAsync({
						id: groupId,
						data: {
							name: values.name,
							filter: advancedSelection ? formattedConditions : [],
							isStatic: !values.advancedSelection,
						},
					})
				: createGroupMutation.mutateAsync({
						name: values.name,
						filter: advancedSelection ? formattedConditions : [],
						isStatic: !values.advancedSelection,
					}));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreateGroupDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Group" : "Create Group"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={isSubmitting || createGroupMutation.isPending || updateGroupMutation.isPending}
				submitButtonText={isEdit ? "Update Group" : "Create Group"}
      	width={advancedSelection ? "540px" : "420px"}
			>
				<ComponentLoader loading={isGroupLoading || isFieldsLoading} />
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								name="name"
								type={DynamicFieldType.TEXT}
								label="Name"
								dynamicField={{
									name: "name",
									label: "Name",
									required: true,
									type: DynamicFieldType.TEXT,
								}}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								name="advancedSelection"
								type={DynamicFieldType.BOOLEAN}
								label="Advanced contact selection by filters"
								disabled={isEdit}
								dynamicField={{
									name: "advancedSelection",
									label: "Advanced contact selection by filters",
									type: DynamicFieldType.BOOLEAN,
								}}
							/>
						</Grid>

						{advancedSelection && !isFieldsLoading && !isGroupLoading && (
							<Grid size={{ xs: 12, md: 12 }}>
								<InputLabel>Filters</InputLabel>
								<Card sx={{ flex: "1 1 auto", overflow: "auto", p: 2, mt: 1 }}>
									<FilterBuilder
										conditions={temporaryConditions}
										onChange={setTemporaryConditions}
										availableFields={availableFields}
									/>
								</Card>
							</Grid>
						)}
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
